import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Alert, AlertDescription, AlertTitle } from "../components/ui/alert";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { toast } from "sonner";
import AdminSidebar from "../components/layout/AdminSidebar";
import { 
  ArrowLeft,
  Settings,
  Shield,
  Lock,
  Unlock,
  AlertTriangle,
  CheckCircle,
  FileText,
  Edit,
  RotateCcw,
  Save,
  Info,
  Sparkles
} from "lucide-react";

const AdminAISettings = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [lockedRequirements, setLockedRequirements] = useState({});
  const [customSettings, setCustomSettings] = useState({});
  const [editSettingsOpen, setEditSettingsOpen] = useState(false);
  const [editTemplateOpen, setEditTemplateOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateDetails, setTemplateDetails] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Editable settings state
  const [settingsForm, setSettingsForm] = useState({
    hoa_name: "",
    hoa_full_name: "",
    board_email: "",
    board_phone: "",
    management_company: "",
    mailing_address: "",
    website: "",
    custom_tone_notes: "",
    custom_header: "",
    custom_footer: ""
  });
  
  // Template customization state
  const [templateCustomizations, setTemplateCustomizations] = useState({
    additional_instructions: "",
    hoa_specific_terms: "",
    additional_citations: "",
    contact_info: "",
    custom_header: "",
    custom_closing: ""
  });

  const fetchData = async () => {
    try {
      const [templatesRes, lockedRes] = await Promise.all([
        axios.get(`${API}/ai/templates`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/ai/admin/locked-requirements`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      setTemplates(templatesRes.data.templates);
      setCustomSettings(templatesRes.data.custom_settings || {});
      setLockedRequirements(lockedRes.data);
      
      // Initialize settings form
      setSettingsForm({
        hoa_name: templatesRes.data.custom_settings?.hoa_name || "",
        hoa_full_name: templatesRes.data.custom_settings?.hoa_full_name || "",
        board_email: templatesRes.data.custom_settings?.board_email || "",
        board_phone: templatesRes.data.custom_settings?.board_phone || "",
        management_company: templatesRes.data.custom_settings?.management_company || "",
        mailing_address: templatesRes.data.custom_settings?.mailing_address || "",
        website: templatesRes.data.custom_settings?.website || "",
        custom_tone_notes: templatesRes.data.custom_settings?.custom_tone_notes || "",
        custom_header: templatesRes.data.custom_settings?.custom_header || "",
        custom_footer: templatesRes.data.custom_settings?.custom_footer || ""
      });
    } catch (error) {
      toast.error("Failed to load AI settings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.role !== "admin" && user?.role !== "board") {
      navigate("/");
      return;
    }
    fetchData();
  }, [token, user]);

  const handleViewTemplate = async (templateName) => {
    try {
      const response = await axios.get(`${API}/ai/templates/${templateName}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTemplateDetails(response.data);
      setSelectedTemplate(templateName);
      setTemplateCustomizations({
        additional_instructions: response.data.customizations?.additional_instructions || "",
        hoa_specific_terms: response.data.customizations?.hoa_specific_terms || "",
        additional_citations: response.data.customizations?.additional_citations || "",
        contact_info: response.data.customizations?.contact_info || "",
        custom_header: response.data.customizations?.custom_header || "",
        custom_closing: response.data.customizations?.custom_closing || ""
      });
      setEditTemplateOpen(true);
    } catch (error) {
      toast.error("Failed to load template details");
    }
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/ai/admin/settings`, settingsForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Settings saved successfully");
      setEditSettingsOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveTemplateCustomizations = async () => {
    if (!selectedTemplate) return;
    
    // Filter out empty customizations
    const nonEmptyCustomizations = Object.fromEntries(
      Object.entries(templateCustomizations).filter(([_, v]) => v.trim() !== "")
    );
    
    if (Object.keys(nonEmptyCustomizations).length === 0) {
      toast.info("No customizations to save");
      return;
    }
    
    setSaving(true);
    try {
      await axios.put(
        `${API}/ai/admin/templates/${selectedTemplate}/customize`,
        nonEmptyCustomizations,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Template customizations saved");
      setEditTemplateOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save customizations");
    } finally {
      setSaving(false);
    }
  };

  const handleResetTemplate = async () => {
    if (!selectedTemplate) return;
    if (!window.confirm(`Reset template "${selectedTemplate}" to default? This will remove all customizations.`)) return;
    
    setSaving(true);
    try {
      await axios.delete(`${API}/ai/admin/templates/${selectedTemplate}/customize`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Template reset to default");
      setEditTemplateOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reset template");
    } finally {
      setSaving(false);
    }
  };

  const getTemplateDisplayName = (name) => {
    return name.split("_").map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
  };

  if (loading) {
    return (
      <AdminSidebar>
        <div className="min-h-screen bg-background flex items-center justify-center">
          <div className="text-muted-foreground">Loading AI settings...</div>
        </div>
      </AdminSidebar>
    );
  }

  return (
    <AdminSidebar>
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b bg-card">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/board")}
              className="text-muted-foreground"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div className="flex items-center gap-3">
              <Settings className="w-6 h-6 text-primary" />
              <span className="font-serif text-lg font-semibold">AI Template Administration</span>
            </div>
          </div>
          {user?.role === "admin" && (
            <Button onClick={() => setEditSettingsOpen(true)}>
              <Edit className="w-4 h-4 mr-2" />
              Edit HOA Settings
            </Button>
          )}
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        {/* Legal Safeguards Alert */}
        <Alert className="mb-8 border-amber-500/50 bg-amber-50">
          <Shield className="h-4 w-4 text-amber-600" />
          <AlertTitle className="text-amber-800">Legal Safeguards Active</AlertTitle>
          <AlertDescription className="text-amber-700">
            Core legal protections are <strong>locked</strong> and cannot be modified. These safeguards protect the HOA and Board from liability.
            You can customize templates with additional instructions, HOA-specific terminology, and contact information.
          </AlertDescription>
        </Alert>

        <Tabs defaultValue="templates" className="w-full">
          <TabsList className="grid w-full grid-cols-3 mb-8">
            <TabsTrigger value="templates">
              <FileText className="w-4 h-4 mr-2" />
              Templates
            </TabsTrigger>
            <TabsTrigger value="locked">
              <Lock className="w-4 h-4 mr-2" />
              Locked Requirements
            </TabsTrigger>
            <TabsTrigger value="settings">
              <Settings className="w-4 h-4 mr-2" />
              HOA Settings
            </TabsTrigger>
          </TabsList>

          {/* Templates Tab */}
          <TabsContent value="templates">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {templates.map((template) => (
                <Card key={template.name} className="hover:shadow-md transition-shadow">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base font-medium">
                        {getTemplateDisplayName(template.name)}
                      </CardTitle>
                      <Sparkles className="w-4 h-4 text-primary" />
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground line-clamp-3 mb-4">
                      {template.description}
                    </p>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-muted-foreground">
                        {template.full_length.toLocaleString()} chars
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleViewTemplate(template.name)}
                      >
                        {user?.role === "admin" ? "Customize" : "View"}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Locked Requirements Tab */}
          <TabsContent value="locked">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="w-5 h-5 text-red-500" />
                  Locked Legal Requirements
                </CardTitle>
                <CardDescription>
                  These requirements are enforced on ALL AI outputs and cannot be removed or modified.
                  They are essential for protecting the HOA and Board from legal liability.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Accordion type="single" collapsible className="w-full">
                  {Object.entries(lockedRequirements.locked_requirements || {}).map(([key, value]) => (
                    <AccordionItem key={key} value={key}>
                      <AccordionTrigger className="text-left">
                        <div className="flex items-center gap-3">
                          <Lock className="w-4 h-4 text-red-500" />
                          <span className="font-medium">{key.split("_").map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(" ")}</span>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent>
                        <div className="space-y-3 pl-7">
                          <div className="p-3 bg-muted rounded-lg">
                            <p className="text-sm font-medium">Locked Text:</p>
                            <p className="text-sm text-muted-foreground mt-1">"{value}"</p>
                          </div>
                          {lockedRequirements.why_locked?.[key] && (
                            <div className="flex items-start gap-2">
                              <Info className="w-4 h-4 text-blue-500 mt-0.5" />
                              <p className="text-sm text-muted-foreground">
                                <strong>Why locked:</strong> {lockedRequirements.why_locked[key]}
                              </p>
                            </div>
                          )}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
                
                {lockedRequirements.legal_basis && (
                  <div className="mt-6 p-4 border rounded-lg bg-blue-50/50">
                    <h4 className="font-medium text-blue-900 mb-2">Legal Basis</h4>
                    <p className="text-sm text-blue-800">{lockedRequirements.legal_basis}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* HOA Settings Tab */}
          <TabsContent value="settings">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Unlock className="w-5 h-5 text-green-500" />
                  Customizable HOA Settings
                </CardTitle>
                <CardDescription>
                  These settings can be customized to match your HOA's specific information.
                  Changes here will be reflected in AI-generated content.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div>
                      <Label className="text-muted-foreground">HOA Name</Label>
                      <p className="font-medium">{customSettings.hoa_name || "Not set"}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Full Legal Name</Label>
                      <p className="font-medium">{customSettings.hoa_full_name || "Not set"}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">State</Label>
                      <p className="font-medium">{customSettings.state || "Not set"}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Board Email</Label>
                      <p className="font-medium">{customSettings.board_email || "Not set"}</p>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <Label className="text-muted-foreground">Board Phone</Label>
                      <p className="font-medium">{customSettings.board_phone || "Not set"}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Management Company</Label>
                      <p className="font-medium">{customSettings.management_company || "Not set"}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Mailing Address</Label>
                      <p className="font-medium">{customSettings.mailing_address || "Not set"}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Website</Label>
                      <p className="font-medium">{customSettings.website || "Not set"}</p>
                    </div>
                  </div>
                </div>
                
                {customSettings.custom_tone_notes && (
                  <div className="mt-6 p-4 border rounded-lg">
                    <Label className="text-muted-foreground">Custom Tone Notes</Label>
                    <p className="mt-1">{customSettings.custom_tone_notes}</p>
                  </div>
                )}
                
                {user?.role === "admin" && (
                  <div className="mt-6">
                    <Button onClick={() => setEditSettingsOpen(true)}>
                      <Edit className="w-4 h-4 mr-2" />
                      Edit Settings
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Edit Settings Dialog */}
        <Dialog open={editSettingsOpen} onOpenChange={setEditSettingsOpen}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Edit HOA Settings</DialogTitle>
              <DialogDescription>
                Customize settings that appear in AI-generated content. Legal safeguards remain enforced.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>HOA Name</Label>
                  <Input
                    value={settingsForm.hoa_name}
                    onChange={(e) => setSettingsForm({...settingsForm, hoa_name: e.target.value})}
                    placeholder="Horse Mountain Ranch HOA"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Full Legal Name</Label>
                  <Input
                    value={settingsForm.hoa_full_name}
                    onChange={(e) => setSettingsForm({...settingsForm, hoa_full_name: e.target.value})}
                    placeholder="Horse Mountain Ranch HOA, Inc."
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Board Email</Label>
                  <Input
                    type="email"
                    value={settingsForm.board_email}
                    onChange={(e) => setSettingsForm({...settingsForm, board_email: e.target.value})}
                    placeholder="board@example.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Board Phone</Label>
                  <Input
                    value={settingsForm.board_phone}
                    onChange={(e) => setSettingsForm({...settingsForm, board_phone: e.target.value})}
                    placeholder="(555) 123-4567"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Management Company</Label>
                <Input
                  value={settingsForm.management_company}
                  onChange={(e) => setSettingsForm({...settingsForm, management_company: e.target.value})}
                  placeholder="ABC Management, LLC"
                />
              </div>
              <div className="space-y-2">
                <Label>Mailing Address</Label>
                <Input
                  value={settingsForm.mailing_address}
                  onChange={(e) => setSettingsForm({...settingsForm, mailing_address: e.target.value})}
                  placeholder="123 Main St, City, CO 12345"
                />
              </div>
              <div className="space-y-2">
                <Label>Website</Label>
                <Input
                  value={settingsForm.website}
                  onChange={(e) => setSettingsForm({...settingsForm, website: e.target.value})}
                  placeholder="https://www.example.com"
                />
              </div>
              <div className="space-y-2">
                <Label>Custom Tone Notes</Label>
                <Textarea
                  value={settingsForm.custom_tone_notes}
                  onChange={(e) => setSettingsForm({...settingsForm, custom_tone_notes: e.target.value})}
                  placeholder="Additional guidance for AI tone and style..."
                  rows={3}
                />
                <p className="text-xs text-muted-foreground">
                  These notes guide the AI's tone but cannot override legal safeguards.
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditSettingsOpen(false)}>Cancel</Button>
              <Button onClick={handleSaveSettings} disabled={saving}>
                {saving ? "Saving..." : "Save Settings"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Template Dialog */}
        <Dialog open={editTemplateOpen} onOpenChange={setEditTemplateOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {user?.role === "admin" ? "Customize" : "View"} Template: {selectedTemplate ? getTemplateDisplayName(selectedTemplate) : ""}
              </DialogTitle>
              <DialogDescription>
                {user?.role === "admin" 
                  ? "Add customizations to this template. Core legal safeguards remain enforced."
                  : "View template details. Contact an admin to make changes."}
              </DialogDescription>
            </DialogHeader>
            
            {templateDetails && (
              <Tabs defaultValue="customize" className="w-full">
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="customize">Customizations</TabsTrigger>
                  <TabsTrigger value="preview">Full Prompt</TabsTrigger>
                </TabsList>
                
                <TabsContent value="customize" className="space-y-4 py-4">
                  {user?.role === "admin" ? (
                    <>
                      <Alert>
                        <Info className="h-4 w-4" />
                        <AlertDescription>
                          Customizations are <strong>added</strong> to the template. They cannot remove or override locked legal requirements.
                        </AlertDescription>
                      </Alert>
                      
                      <div className="space-y-2">
                        <Label>Additional Instructions</Label>
                        <Textarea
                          value={templateCustomizations.additional_instructions}
                          onChange={(e) => setTemplateCustomizations({...templateCustomizations, additional_instructions: e.target.value})}
                          placeholder="Add specific instructions for this template type..."
                          rows={3}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>HOA-Specific Terms</Label>
                        <Textarea
                          value={templateCustomizations.hoa_specific_terms}
                          onChange={(e) => setTemplateCustomizations({...templateCustomizations, hoa_specific_terms: e.target.value})}
                          placeholder="Define specific terms used in your HOA (e.g., 'Common Area A refers to the main park')"
                          rows={2}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Additional Citations to Include</Label>
                        <Textarea
                          value={templateCustomizations.additional_citations}
                          onChange={(e) => setTemplateCustomizations({...templateCustomizations, additional_citations: e.target.value})}
                          placeholder="Specific rule sections that should always be referenced..."
                          rows={2}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Contact Information to Include</Label>
                        <Input
                          value={templateCustomizations.contact_info}
                          onChange={(e) => setTemplateCustomizations({...templateCustomizations, contact_info: e.target.value})}
                          placeholder="Board contact info for letters..."
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Custom Header</Label>
                          <Textarea
                            value={templateCustomizations.custom_header}
                            onChange={(e) => setTemplateCustomizations({...templateCustomizations, custom_header: e.target.value})}
                            placeholder="Custom letterhead or header..."
                            rows={2}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Custom Closing</Label>
                          <Textarea
                            value={templateCustomizations.custom_closing}
                            onChange={(e) => setTemplateCustomizations({...templateCustomizations, custom_closing: e.target.value})}
                            placeholder="Custom closing text..."
                            rows={2}
                          />
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      <Lock className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>Only administrators can customize templates.</p>
                    </div>
                  )}
                </TabsContent>
                
                <TabsContent value="preview" className="py-4">
                  <div className="p-4 bg-muted rounded-lg max-h-[400px] overflow-y-auto">
                    <pre className="text-xs whitespace-pre-wrap font-mono">
                      {templateDetails.prompt}
                    </pre>
                  </div>
                  
                  <div className="mt-4 p-3 border rounded-lg bg-amber-50">
                    <div className="flex items-center gap-2 text-amber-800">
                      <Shield className="w-4 h-4" />
                      <span className="font-medium">Locked Requirements Active</span>
                    </div>
                    <p className="text-sm text-amber-700 mt-1">
                      This template includes all mandatory legal safeguards including the disclaimer, 
                      no-legal-advice rule, and board approval requirements.
                    </p>
                  </div>
                </TabsContent>
              </Tabs>
            )}
            
            <DialogFooter className="flex justify-between">
              {user?.role === "admin" && templateDetails?.is_customized && (
                <Button variant="outline" onClick={handleResetTemplate} disabled={saving}>
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Reset to Default
                </Button>
              )}
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setEditTemplateOpen(false)}>
                  {user?.role === "admin" ? "Cancel" : "Close"}
                </Button>
                {user?.role === "admin" && (
                  <Button onClick={handleSaveTemplateCustomizations} disabled={saving}>
                    <Save className="w-4 h-4 mr-2" />
                    {saving ? "Saving..." : "Save Customizations"}
                  </Button>
                )}
              </div>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
    </AdminSidebar>
  );
};

export default AdminAISettings;
